import React, { useState } from 'react';
import { X, Upload, Link as LinkIcon } from 'lucide-react';
import { uploadVideo } from '../services/uploadService';

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoUploaded: (videoUrl: string, videoType: 'youtube' | 'vimeo' | 'mp4') => void;
  workspaceId: string;
  projectId: string;
  currentVideoUrl?: string;
}

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  isOpen,
  onClose,
  onVideoUploaded,
  workspaceId,
  projectId,
  currentVideoUrl
}) => {
  const [activeTab, setActiveTab] = useState<'url' | 'file'>('url');
  const [videoUrl, setVideoUrl] = useState(currentVideoUrl || '');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const detectVideoType = (url: string): 'youtube' | 'vimeo' | 'mp4' | null => {
    if (!url) return null;

    // YouTube detection
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }

    // Vimeo detection
    if (url.includes('vimeo.com')) {
      return 'vimeo';
    }

    // MP4 URL detection
    if (url.endsWith('.mp4') || url.includes('.mp4?')) {
      return 'mp4';
    }

    return null;
  };

  const handleUrlSubmit = () => {
    const videoType = detectVideoType(videoUrl);

    if (!videoType) {
      setError('Please enter a valid YouTube, Vimeo, or MP4 URL');
      return;
    }

    setError('');
    onVideoUploaded(videoUrl, videoType);
    onClose();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file');
      return;
    }

    // Validate file size (max 500MB)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      setError('Video file size must be less than 500MB');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      const uploadedVideo = await uploadVideo(
        workspaceId,
        projectId,
        file,
        'intro-video',
        'project',
        (progress: number) => {
          setUploadProgress(progress);
        }
      );

      // For uploaded files, return the S3 path instead of presigned URL
      onVideoUploaded(uploadedVideo.path, 'mp4');
      onClose();
    } catch (err: any) {
      console.error('Error uploading video:', err);
      setError(err.message || 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setVideoUrl('');
      setError('');
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content video-upload-modal">
        <div className="modal-header">
          <h2>Add Intro Video</h2>
          <button className="modal-close-btn" onClick={handleClose} disabled={uploading}>
            <X size={20} />
          </button>
        </div>

        <div className="video-upload-tabs">
          <button
            className={`video-upload-tab ${activeTab === 'url' ? 'active' : ''}`}
            onClick={() => setActiveTab('url')}
            disabled={uploading}
          >
            <LinkIcon size={18} />
            Video URL
          </button>
          <button
            className={`video-upload-tab ${activeTab === 'file' ? 'active' : ''}`}
            onClick={() => setActiveTab('file')}
            disabled={uploading}
          >
            <Upload size={18} />
            Upload File
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message" style={{ marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {activeTab === 'url' ? (
            <div className="video-url-form">
              <label className="form-label">
                Video URL
                <span style={{ color: '#6B7280', fontSize: '13px', marginLeft: '8px' }}>
                  (YouTube, Vimeo, or MP4 URL)
                </span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="https://www.youtube.com/watch?v=..."
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                  setError('');
                }}
                disabled={uploading}
              />
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>
                Supported formats:
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>YouTube: https://www.youtube.com/watch?v=VIDEO_ID</li>
                  <li>Vimeo: https://vimeo.com/VIDEO_ID</li>
                  <li>Direct MP4: https://example.com/video.mp4</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="video-file-upload">
              <div
                className="video-upload-dropzone"
                onClick={() => document.getElementById('video-file-input')?.click()}
              >
                <Upload size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                  Click to upload video file
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                  MP4, WebM, or other video formats (max 500MB)
                </p>
                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="progress-text">{uploadProgress}%</span>
                  </div>
                )}
              </div>
              <input
                id="video-file-input"
                type="file"
                accept="video/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </button>
          {activeTab === 'url' && (
            <button
              className="btn btn-primary"
              onClick={handleUrlSubmit}
              disabled={!videoUrl || uploading}
            >
              Add Video
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUploadModal;
